import React from 'react';
import { Alert, Box, Grid, Stack, Typography } from '@mui/material';
import PpmFilterBar from './PpmFilterBar';
import PpmGrid from './PpmGrid';
import PpmLinesGrid from './PpmLinesGrid';
import { approvePpm, fetchPpmCards, fetchPpmLines, fetchPpms, generatePpm, generatePpmCalendar, savePpmLines, submitPpm } from './ppmApi';
import PpmSummaryPanel from './PpmSummaryPanel';

type State = {
  fiscalYear: string; budgetVersionId: number | ''; ppmName: string; status: string; notes: string;
  loading: boolean; error: string; success: string; ppms: any[]; selectedPpm: any | null; lines: any[]; cards: any;
};

class PpmWorkbench extends React.Component<any, State> {
  state: State = { fiscalYear: '2026', budgetVersionId: '', ppmName: 'PPM 2026', status: '', notes: '', loading: false, error: '', success: '', ppms: [], selectedPpm: null, lines: [], cards: {} };

  componentDidMount() { this.load(); }

  load = async () => {
    this.setState({ loading: true, error: '', success: '' });
    try {
      const ppms = await fetchPpms(this.state.fiscalYear, this.state.status);
      const selectedPpm = ppms.length ? ppms[0] : null;
      const [lines, cards] = selectedPpm ? await Promise.all([fetchPpmLines(selectedPpm.rid), fetchPpmCards(selectedPpm.rid)]) : [[], {}];
      this.setState({ ppms, selectedPpm, lines, cards });
    } catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  selectPpm = async (selectedPpm: any) => {
    this.setState({ selectedPpm, loading: true, error: '', success: '' });
    try {
      const [lines, cards] = await Promise.all([fetchPpmLines(selectedPpm.rid), fetchPpmCards(selectedPpm.rid)]);
      this.setState({ lines, cards });
    } catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  generate = async () => {
    if (!this.state.budgetVersionId) { this.setState({ error: 'Budget Version ID is required.' }); return; }
    this.setState({ loading: true, error: '', success: '' });
    try { await generatePpm(Number(this.state.budgetVersionId), this.state.ppmName); this.setState({ success: 'PPM generated.' }); await this.load(); }
    catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  calendarize = async () => {
    if (!this.state.selectedPpm) return;
    this.setState({ loading: true, error: '', success: '' });
    try { await generatePpmCalendar(this.state.selectedPpm.rid); this.setState({ success: 'PPM calendar generated.' }); await this.selectPpm(this.state.selectedPpm); }
    catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  submit = async () => {
    if (!this.state.selectedPpm) return;
    this.setState({ loading: true, error: '', success: '' });
    try { await submitPpm(this.state.selectedPpm.rid, this.state.notes); this.setState({ success: 'PPM submitted.' }); await this.load(); }
    catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  approve = async () => {
    if (!this.state.selectedPpm) return;
    this.setState({ loading: true, error: '', success: '' });
    try { await approvePpm(this.state.selectedPpm.rid, this.state.notes); this.setState({ success: 'PPM approved.' }); await this.load(); }
    catch (err: any) { this.setState({ error: err.message || String(err) }); }
    finally { this.setState({ loading: false }); }
  };

  saveLines = async (rows: any[]) => {
    if (!this.state.selectedPpm) return;
    const payload = rows.filter((r: any) => r._state && r._state !== 'clean');
    await savePpmLines(this.state.selectedPpm.rid, payload);
    await this.selectPpm(this.state.selectedPpm);
  };

  render() {
    const s = this.state;
    return (
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>PPM Workbench</Typography>
            <Typography variant="body2" color="text.secondary">Generate, refine, calendarize and approve procurement plans.</Typography>
          </Box>

          <PpmFilterBar
            fiscalYear={s.fiscalYear}
            budgetVersionId={s.budgetVersionId}
            ppmName={s.ppmName}
            status={s.status}
            notes={s.notes}
            selectedPpm={s.selectedPpm}
            loading={s.loading}
            onFiscalYearChange={(fiscalYear) => this.setState({ fiscalYear })}
            onBudgetVersionIdChange={(budgetVersionId) => this.setState({ budgetVersionId })}
            onPpmNameChange={(ppmName) => this.setState({ ppmName })}
            onStatusChange={(status) => this.setState({ status })}
            onNotesChange={(notes) => this.setState({ notes })}
            onRefresh={this.load}
            onGenerate={this.generate}
            onCalendarize={this.calendarize}
            onSubmit={this.submit}
            onApprove={this.approve}
          />

          {s.error && <Alert severity="error">{s.error}</Alert>}
          {s.success && <Alert severity="success">{s.success}</Alert>}

          <PpmSummaryPanel cards={s.cards} />

          <Grid container spacing={3}>
            <Grid size={{ xs: 12 }}>
              <PpmGrid rows={s.ppms} selectedId={s.selectedPpm?.rid} loading={s.loading} onSelect={this.selectPpm} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PpmLinesGrid rows={s.lines} loading={s.loading} onChange={(lines) => this.setState({ lines })} onSubmit={this.saveLines} />
            </Grid>
          </Grid>
        </Stack>
      </Box>
    );
  }
}
export default PpmWorkbench;
